import { useFeatureExtractionContext } from '@mobile-mapping/providers';
import {
  isLayerLineStyle,
  type LayerLineStyle,
  type MapLayer,
} from '@trimble/geospatial';
import { t } from '@trimble/i18n';
import {
  LayerListPanel,
  type LayerCategoryKey,
  type LayerItemObject,
} from '@trimble/layer-list-panel';
import type { Layer } from '@trimble/web3d-providers';
import { Component, createMemo, JSX, Show } from 'solid-js';

const EXPANDED_CATEGORIES: readonly LayerCategoryKey[] = [];
const DEFAULT_LINE_STYLE: LayerLineStyle = 'solid';
const DEFAULT_LINE_WEIGHT = 2;

// The panel's edit popup reports numeric layer ids, so each row gets one.
const toNumericId = (index: number): number => index + 1;

const toLayerItem = (
  layer: MapLayer,
  index: number,
  visible: boolean
): LayerItemObject => {
  const lineStyle = layer.style.lineStyle ?? DEFAULT_LINE_STYLE;
  const lineWeight = layer.style.lineWeight ?? DEFAULT_LINE_WEIGHT;
  const object: Layer = {
    id: String(toNumericId(index)),
    guid: layer.id,
    name: layer.name,
    color: layer.style.color,
    lineStyle,
    lineThickness: lineWeight,
    visible,
    // Locks the popup's name field; renaming Maps layers is not supported here.
    isDefault: true,
  };

  return {
    id: layer.id,
    guid: layer.id,
    name: layer.name,
    color: layer.style.color,
    lineType: lineStyle,
    lineWeight,
    object,
    visible,
    editable: true,
    isSurface: false,
    isIsolated: false,
    isDefault: false,
    category: 'feature',
  };
};

export const LayersTab: Component = (): JSX.Element => {
  const featureExtraction = useFeatureExtractionContext();

  const items = createMemo((): LayerItemObject[] =>
    featureExtraction.editingState.layers.map((layer, index) =>
      toLayerItem(layer, index, featureExtraction.isLayerVisible(layer.id))
    )
  );

  const toLayerIds = (numericIds: readonly number[]): string[] => {
    const layers = featureExtraction.editingState.layers;
    return (
      numericIds
        .map((numericId) => layers[numericId - 1]?.id)
        // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
        .filter((layerId): layerId is string => layerId !== undefined)
    );
  };

  const handleItemActivate = (item: LayerItemObject): void => {
    featureExtraction.setSelectedLayerId(item.id);
  };

  const handleToggledItemVisibility = (
    layerItems: LayerItemObject[],
    visibility: boolean
  ): void => {
    for (const item of layerItems) {
      if (featureExtraction.isLayerVisible(item.id) !== visibility) {
        featureExtraction.toggleLayerVisibility(item.id);
      }
    }
  };

  const handleToggleAllLayersVisibility = (visible: boolean): void => {
    featureExtraction.setVisibleLayerIds(
      visible ? featureExtraction.editingState.layers.map((l) => l.id) : []
    );
  };

  const handleLayerColorChange = (
    numericIds: number[],
    color: string
  ): void => {
    featureExtraction.setLayerStyle(toLayerIds(numericIds), { color });
  };

  const handleLayerLineStyleChange = (
    numericIds: number[],
    lineStyle: string
  ): void => {
    if (isLayerLineStyle(lineStyle)) {
      featureExtraction.setLayerStyle(toLayerIds(numericIds), { lineStyle });
    }
  };

  const handleLayerLineThicknessChange = (
    numericIds: number[],
    lineWeight: number
  ): void => {
    featureExtraction.setLayerStyle(toLayerIds(numericIds), { lineWeight });
  };

  return (
    <div class="relative h-full w-full" data-testid="mm-layers-tab">
      <Show
        when={items().length > 0}
        fallback={
          <div
            class="flex h-full flex-col items-center justify-center text-center"
            data-testid="mm-layers-tab-empty"
          >
            <modus-wc-icon name="layer" size="lg" />
            <h3
              class="mb-2 font-semibold"
              textContent={t('mobileMapping.layers.noLayersTitle')}
            />
            <p
              class="w-60"
              textContent={t('mobileMapping.layers.noLayersMessage')}
            />
          </div>
        }
      >
        <LayerListPanel
          isViewOnly={false}
          designId={undefined}
          items={items()}
          activeItemId={featureExtraction.editingState.selectedLayerId}
          handleItemActivate={handleItemActivate}
          loadingIds={[]}
          initiallyCollapsedCategoryKeys={EXPANDED_CATEGORIES}
          handleToggledItemVisibility={handleToggledItemVisibility}
          handleToggleAllLayersVisibility={handleToggleAllLayersVisibility}
          handleLayerColorChange={handleLayerColorChange}
          handleLayerLineStyleChange={handleLayerLineStyleChange}
          handleLayerLineThicknessChange={handleLayerLineThicknessChange}
        />
      </Show>
    </div>
  );
};
